import Phaser from "phaser";
import { spriteKeyFor, packFor, PACK_LABELS, type PackSlug } from "../fighters/loader";

/**
 * DemoPreview — панель «как боец двигается»: спрайт + цикл по всем анимациям
 * с подписью текущего движения и лейблом пака. Используется в SelectScene.
 *
 * Анимации регистрируются один раз через ensureAnimsForPack().
 */

// Только реально-разные кадры для боксёра (kick/sweep/throw в OGA-паке нет —
// были бы proxy-копии тех же punch'ей, поэтому скрыты).
const SEQUENCE: Array<{ anim: string; label: string; ms: number }> = [
  { anim: "idle",      label: "СТОЙКА",        ms: 1500 },
  { anim: "walk",      label: "ХОДЬБА",        ms: 1500 },
  { anim: "run",       label: "БЕГ",           ms: 1300 },
  { anim: "jab",       label: "ДЖЭБ",          ms: 800 },
  { anim: "straight",  label: "ПРЯМОЙ",        ms: 850 },
  { anim: "hp",        label: "ТЯЖЁЛЫЙ ХУК",   ms: 900 },
  { anim: "uppercut",  label: "АППЕРКОТ",      ms: 1100 },
  { anim: "block",     label: "БЛОК",          ms: 1000 },
  { anim: "hit",       label: "ПОЛУЧАЕТ",      ms: 700 },
  { anim: "knockdown", label: "НОКДАУН",       ms: 1100 },
  { anim: "win",       label: "ПОБЕДА",        ms: 1500 },
];

export function ensureAnimsForPack(scene: Phaser.Scene, pack: PackSlug) {
  const sheetKey = `fighter:${pack}`;
  const metaKey = `fighter-meta:${pack}`;
  const meta = scene.cache.json.get(metaKey);
  if (!meta) return;
  const animations = meta.animations as Record<string, { frames: number[]; frameRate: number; repeat: number }>;
  for (const [animName, def] of Object.entries(animations)) {
    const key = `anim:${pack}:${animName}`;
    if (scene.anims.exists(key)) continue;
    scene.anims.create({
      key,
      frames: def.frames.map((f) => ({ key: sheetKey, frame: f })),
      frameRate: def.frameRate,
      repeat: def.repeat,
    });
  }
}

export class DemoPreview {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Rectangle;
  private border: Phaser.GameObjects.Rectangle;
  private nameLabel: Phaser.GameObjects.Text;
  private packLabel: Phaser.GameObjects.Text;
  private sprite: Phaser.GameObjects.Sprite;
  private moveLabel: Phaser.GameObjects.Text;
  private hintLabel: Phaser.GameObjects.Text;

  private timerEvent?: Phaser.Time.TimerEvent;
  private currentSlug?: string;
  private currentBandana = 0xc41e3a;
  private seqIdx = 0;
  private destroyed = false;

  readonly w: number;
  readonly h: number;

  constructor(scene: Phaser.Scene, x: number, y: number, w: number, h: number) {
    this.scene = scene;
    this.w = w;
    this.h = h;
    this.container = scene.add.container(x, y);

    // Внешняя золотая рамка + чёрная подложка (UMK-стиль)
    this.border = scene.add.rectangle(0, 0, w, h, 0xffd700).setOrigin(0.5);
    this.bg = scene.add.rectangle(0, 0, w - 6, h - 6, 0x140404).setOrigin(0.5);

    // Имя бойца сверху
    this.nameLabel = scene.add.text(0, -h / 2 + 16, "—", {
      fontFamily: "Arial Black", fontSize: "16px",
      color: "#ffffff", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    // Пак-лейбл (УЛИЧНЫЙ / РЕЙНДЖЕР / ВОИН)
    this.packLabel = scene.add.text(0, -h / 2 + 36, "", {
      fontFamily: "Arial Black", fontSize: "12px",
      color: "#ffd700", stroke: "#000", strokeThickness: 3,
    }).setOrigin(0.5);

    // Спрайт по центру
    this.sprite = scene.add.sprite(0, 12, "")
      .setOrigin(0.5, 0.5).setScale(0.85);

    // Текущая анимация
    this.moveLabel = scene.add.text(0, h / 2 - 38, "", {
      fontFamily: "Arial Black", fontSize: "20px",
      color: "#ffffff", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    // Подсказка
    this.hintLabel = scene.add.text(0, h / 2 - 14, "ДЕМО — все приёмы по кругу", {
      fontFamily: "monospace", fontSize: "10px", color: "#888888",
    }).setOrigin(0.5);

    this.container.add([
      this.border, this.bg,
      this.nameLabel, this.packLabel,
      this.sprite,
      this.moveLabel, this.hintLabel,
    ]);
  }

  /** Переключить превью на нового бойца. Без рестарта если slug+цвет не изменились. */
  setFighter(slug: string, bandanaColor: number, displayName?: string) {
    if (this.destroyed) return;
    if (this.currentSlug === slug && this.currentBandana === bandanaColor) return;

    this.currentSlug = slug;
    this.currentBandana = bandanaColor;

    const pack = packFor(slug);
    ensureAnimsForPack(this.scene, pack);

    const packTitle = PACK_LABELS[pack];
    const meta = this.scene.cache.json.get(`fighter-meta:${pack}`);
    const fallbackName = (meta?.name as string | undefined) ?? pack.toUpperCase();
    this.nameLabel.setText((displayName ?? fallbackName).toUpperCase());
    this.packLabel.setText(`ПАК: ${packTitle}`);

    this.sprite.setTexture(spriteKeyFor(slug), 0);
    this.sprite.clearTint();
    void bandanaColor;

    this.seqIdx = 0;
    this.playNext();
  }

  private playNext() {
    if (this.destroyed || !this.currentSlug) return;
    const step = SEQUENCE[this.seqIdx];
    const pack = packFor(this.currentSlug);
    const animKey = `anim:${pack}:${step.anim}`;

    if (this.scene.anims.exists(animKey)) {
      this.sprite.play(animKey, true);
      this.moveLabel.setText(step.label);
    } else {
      this.moveLabel.setText("");
    }

    this.seqIdx = (this.seqIdx + 1) % SEQUENCE.length;

    if (this.timerEvent) this.timerEvent.remove();
    this.timerEvent = this.scene.time.delayedCall(step.ms, () => this.playNext());
  }

  destroy() {
    this.destroyed = true;
    if (this.timerEvent) {
      this.timerEvent.remove();
      this.timerEvent = undefined;
    }
    this.container.destroy();
  }
}
